/**
 * i18n del CHROME — Fase 1 (odd/tasks/i18n-chrome.md).
 *
 * Decisión Rafael (2026-10-10, opción A): el chrome de la app (menús, botones y
 * controles propios, mensajes del sistema de NUESTRAS pantallas) se muestra en
 * el idioma del DISPOSITIVO de forma automática — ES (default) / EN / PT-BR
 * (pedido Rafael 2026-10-10: "está llamativo para los de Brazil"). Los textos
 * de NEGOCIO (réplica exacta GX: "Prestamos", captions, mensajes de validación,
 * labels de datos, columnas de tablas de negocio) NO se traducen: por diseño
 * `t()` solo traduce lo que esté en el diccionario, así cualquier texto sin
 * entrada queda en español (fallback ES natural — riesgo cero al retrofitear).
 *
 * Sin librerías externas ni contexto React: el idioma es automático por
 * dispositivo (no hay selector) y se lee UNA vez al cargar el módulo.
 */

export type Idioma = 'es' | 'en' | 'pt'

// Detección: `navigator.language` empieza con "es" → ES; con "pt" → PT-BR;
// cualquier otro → EN.
const idiomaCrudo = (navigator.language || '').toLowerCase()
const idioma: Idioma = idiomaCrudo.startsWith('es')
  ? 'es'
  : idiomaCrudo.startsWith('pt')
    ? 'pt'
    : 'en'

// Refleja el idioma detectado en el atributo lang del documento (accesibilidad).
// Guard `typeof document`: permite ejecutar el módulo en tests/entornos sin DOM.
if (typeof document !== 'undefined') {
  document.documentElement.lang = idioma
}

/**
 * Diccionario clave = texto en español (el MISMO literal que está en el JSX) →
 * valor = inglés. Organizado por superficie del retrofit. Las palabras que ya
 * son idénticas en inglés (Roles, Capital, SMS) NO llevan entrada: el fallback
 * las deja tal cual. Los nombres de marca (SiprinIA, TronIA, Data Siprin) y los
 * textos de negocio/GX tampoco llevan entrada — jamás se traducen.
 */
const TRADUCCIONES: Record<string, string> = {
  // ── AppSidebar (labels de navegación del backend /api/menu + grupos) ──
  Resumen: 'Overview',
  Empresas: 'Companies',
  Usuarios: 'Users',
  Planes: 'Plans',
  Módulos: 'Modules',
  Países: 'Countries',
  Impresoras: 'Printers',
  Pagos: 'Payments',
  Sesiones: 'Sessions',
  Actualizaciones: 'Updates',
  Rutas: 'Routes',
  'Gestión Avanzada': 'Advanced Management',
  Clientes: 'Clients',
  Prestamos: 'Loans',
  Créditos: 'Credits',
  Abonos: 'Payments',
  Atrasos: 'Overdue',
  Productos: 'Products',
  Facturas: 'Invoices',
  Cierre: 'Daily close',
  Egresos: 'Expenses',
  Cajas: 'Cash boxes',
  Informes: 'Reports',
  'Mi Cuenta': 'My Account',
  'Mi Clave': 'My Password',
  Supervisores: 'Supervisors',
  Cobradores: 'Collectors',
  Socios: 'Partners',
  Salir: 'Log out',
  // Grupos del menú
  Registros: 'Records',
  Facturación: 'Billing',
  Contabilidad: 'Accounting',
  Configuración: 'Settings',

  // ── AppHeader (buscador, Herramientas, notificaciones, soporte) ──
  Buscar: 'Search',
  'Abrir buscador': 'Open search dialog',
  'Buscar cliente, préstamo o ruta...': 'Search client, loan or route...',
  'Escribe al menos 2 letras: cédula o nombre del cliente, número de préstamo, o nombre/código de la ruta.':
    'Type at least 2 characters: client ID or name, loan number, or route name/code.',
  'Sin resultados.': 'No results.',
  Herramientas: 'Tools',
  Auditoría: 'Audit',
  'Clima/Divisas': 'Weather/Currencies',
  Notificaciones: 'Notifications',
  'Sin novedades': 'No updates',
  Descargar: 'Download',
  Descartar: 'Dismiss',
  'Contactar a soporte': 'Contact support',
  Asunto: 'Subject',
  'Escribe tu mensaje...': 'Write your message...',
  Cerrar: 'Close',
  'Enviando...': 'Sending...',
  Enviar: 'Send',
  'Mensaje enviado.': 'Message sent.',
  'No se pudo enviar el mensaje.': 'Could not send the message.',

  // ── LoginPage (formulario propio + textos de marca del panel izquierdo) ──
  'Plataforma inteligente de gestión de cobranza y administración de préstamos':
    'Intelligent platform for debt collection and loan management',
  'Controle su cartera, rutas, abonos y cobradores desde una sola plataforma.':
    'Manage your portfolio, routes, payments and collectors from a single platform.',
  'Descargar APK Cobrador': 'Download Collector APK',
  'Iniciar sesión': 'Sign in',
  'Ingrese sus credenciales para acceder al sistema':
    'Enter your credentials to access the system',
  Usuario: 'Username',
  Contraseña: 'Password',
  'Mostrar contraseña': 'Show password',
  'Ocultar contraseña': 'Hide password',
  Recordarme: 'Remember me',
  '¿Olvidó su Contraseña?': 'Forgot your password?',
  '¿No tienes cuenta?': "Don't have an account?",
  Registrarse: 'Sign up',

  // ── WwGrid (controles del componente — NUNCA títulos ni labels de datos) ──
  'Filtro principal': 'Main filter',
  Agregar: 'Add',
  Exportar: 'Export',
  'Selecciona columnas': 'Select columns',
  'Buscar columna': 'Search column',
  'Fijas a la izquierda': 'Pinned left',
  'No fijas': 'Not pinned',
  'Fijas a la derecha': 'Pinned right',
  '(Ninguna)': '(None)',
  'Restaurar la configuración predeterminada': 'Restore default configuration',
  Actualizar: 'Update',
  Grilla: 'Grid',
  'Tarjetas pequeñas': 'Small cards',
  'Tarjetas con info': 'Info cards',
  'Tarjetas grandes': 'Large cards',
  Filtros: 'Filters',
  'Limpiar filtros': 'Clear filters',
  'Guardar filtro como...': 'Save filter as...',
  'Guardar filtro como... — pendiente de réplica': 'Save filter as... — not yet available',
  'Quitar filtro': 'Remove filter',
  Opciones: 'Options',
  'Ordenar de A a Z': 'Sort A to Z',
  'Ordenar de Z a A': 'Sort Z to A',
  'Quitar fijación': 'Unpin',
  'Fijar a la izquierda': 'Pin left',
  'Fijar a la derecha': 'Pin right',
  Acciones: 'Actions',
  Volver: 'Back',
  'Limpiar búsqueda': 'Clear search',
  Aplicar: 'Apply',
  Desde: 'From',
  Hasta: 'To',
  '(Vacío)': '(Empty)',
  '- No se encontraron resultados -': '- No results found -',
  'No se encontraron registros': 'No records found',
  'No se pudo exportar': 'Could not export',
  // Paginación: frases compuestas con t() en WwGrid
  // (`${t('Página')} 1 ${t('de')} 3`, `${n} ${t('filas')}`).
  Página: 'Page',
  de: 'of',
  'Registros por página:': 'Rows per page:',
  filas: 'rows',
  'Ir a página:': 'Go to page:',
  Ir: 'Go',
  'Filtrando por:': 'Filtering by:',
  Anterior: 'Previous',
  Siguiente: 'Next',
}

/**
 * Diccionario Portugués (Brasil) — clave = el mismo texto en español del JSX
 * (mismas claves que el diccionario EN). Traducción PT-BR natural y
 * profesional (pedido Rafael 2026-10-10: "está llamativo para los de Brazil").
 * Revisable: cualquier ajuste de términos se hace solo aquí. Nota: "Abonos" →
 * "Recebimentos" para no chocar con "Pagos" → "Pagamentos" (el EN dejó ambos
 * en "Payments" — revisable también).
 */
const TRADUCCIONES_PT: Record<string, string> = {
  // ── AppSidebar (labels de navegación del backend /api/menu + grupos) ──
  Resumen: 'Visão Geral',
  Empresas: 'Empresas',
  Usuarios: 'Usuários',
  Planes: 'Planos',
  Módulos: 'Módulos',
  Países: 'Países',
  Impresoras: 'Impressoras',
  Pagos: 'Pagamentos',
  Sesiones: 'Sessões',
  Actualizaciones: 'Atualizações',
  Rutas: 'Rotas',
  'Gestión Avanzada': 'Gestão Avançada',
  Clientes: 'Clientes',
  Prestamos: 'Empréstimos',
  Créditos: 'Créditos',
  Abonos: 'Recebimentos',
  Atrasos: 'Atrasos',
  Productos: 'Produtos',
  Facturas: 'Faturas',
  Cierre: 'Fechamento',
  Egresos: 'Despesas',
  Cajas: 'Caixas',
  Informes: 'Relatórios',
  'Mi Cuenta': 'Minha Conta',
  'Mi Clave': 'Minha Senha',
  Supervisores: 'Supervisores',
  Cobradores: 'Cobradores',
  Socios: 'Sócios',
  Salir: 'Sair',
  // Grupos del menú
  Registros: 'Registros',
  Facturación: 'Faturamento',
  Contabilidad: 'Contabilidade',
  Configuración: 'Configurações',

  // ── AppHeader (buscador, Herramientas, notificaciones, soporte) ──
  Buscar: 'Buscar',
  'Abrir buscador': 'Abrir busca',
  'Buscar cliente, préstamo o ruta...': 'Buscar cliente, empréstimo ou rota...',
  'Escribe al menos 2 letras: cédula o nombre del cliente, número de préstamo, o nombre/código de la ruta.':
    'Digite pelo menos 2 letras: cédula ou nome do cliente, número do empréstimo, ou nome/código da rota.',
  'Sin resultados.': 'Sem resultados.',
  Herramientas: 'Ferramentas',
  Auditoría: 'Auditoria',
  'Clima/Divisas': 'Clima/Moedas',
  Notificaciones: 'Notificações',
  'Sin novedades': 'Sem novidades',
  Descargar: 'Baixar',
  Descartar: 'Descartar',
  'Contactar a soporte': 'Contatar o suporte',
  Asunto: 'Assunto',
  'Escribe tu mensaje...': 'Escreva sua mensagem...',
  Cerrar: 'Fechar',
  'Enviando...': 'Enviando...',
  Enviar: 'Enviar',
  'Mensaje enviado.': 'Mensagem enviada.',
  'No se pudo enviar el mensaje.': 'Não foi possível enviar a mensagem.',

  // ── LoginPage (formulario propio + textos de marca del panel izquierdo) ──
  'Plataforma inteligente de gestión de cobranza y administración de préstamos':
    'Plataforma inteligente de gestão de cobrança e administração de empréstimos',
  'Controle su cartera, rutas, abonos y cobradores desde una sola plataforma.':
    'Controle sua carteira, rotas, recebimentos e cobradores em uma única plataforma.',
  'Descargar APK Cobrador': 'Baixar APK Cobrador',
  'Iniciar sesión': 'Entrar',
  'Ingrese sus credenciales para acceder al sistema':
    'Digite suas credenciais para acessar o sistema',
  Usuario: 'Usuário',
  Contraseña: 'Senha',
  'Mostrar contraseña': 'Mostrar senha',
  'Ocultar contraseña': 'Ocultar senha',
  Recordarme: 'Lembrar de mim',
  '¿Olvidó su Contraseña?': 'Esqueceu sua Senha?',
  '¿No tienes cuenta?': 'Não tem uma conta?',
  Registrarse: 'Cadastre-se',

  // ── WwGrid (controles del componente — NUNCA títulos ni labels de datos) ──
  'Filtro principal': 'Filtro principal',
  Agregar: 'Adicionar',
  Exportar: 'Exportar',
  'Selecciona columnas': 'Selecionar colunas',
  'Buscar columna': 'Buscar coluna',
  'Fijas a la izquierda': 'Fixadas à esquerda',
  'No fijas': 'Não fixadas',
  'Fijas a la derecha': 'Fixadas à direita',
  '(Ninguna)': '(Nenhuma)',
  'Restaurar la configuración predeterminada': 'Restaurar configuração padrão',
  Actualizar: 'Atualizar',
  Grilla: 'Grade',
  'Tarjetas pequeñas': 'Cards pequenos',
  'Tarjetas con info': 'Cards com info',
  'Tarjetas grandes': 'Cards grandes',
  Filtros: 'Filtros',
  'Limpiar filtros': 'Limpar filtros',
  'Guardar filtro como...': 'Salvar filtro como...',
  'Guardar filtro como... — pendiente de réplica': 'Salvar filtro como... — indisponível',
  'Quitar filtro': 'Remover filtro',
  Opciones: 'Opções',
  'Ordenar de A a Z': 'Ordenar de A a Z',
  'Ordenar de Z a A': 'Ordenar de Z a A',
  'Quitar fijación': 'Desafixar',
  'Fijar a la izquierda': 'Fixar à esquerda',
  'Fijar a la derecha': 'Fixar à direita',
  Acciones: 'Ações',
  Volver: 'Voltar',
  'Limpiar búsqueda': 'Limpar busca',
  Aplicar: 'Aplicar',
  Desde: 'De',
  Hasta: 'Até',
  '(Vacío)': '(Vazio)',
  '- No se encontraron resultados -': '- Nenhum resultado encontrado -',
  'No se encontraron registros': 'Nenhum registro encontrado',
  'No se pudo exportar': 'Não foi possível exportar',
  // Paginación
  Página: 'Página',
  de: 'de',
  'Registros por página:': 'Registros por página:',
  filas: 'linhas',
  'Ir a página:': 'Ir para página:',
  Ir: 'Ir',
  'Filtrando por:': 'Filtrando por:',
  Anterior: 'Anterior',
  Siguiente: 'Próximo',
}

/**
 * Traduce un texto de CHROME al idioma del dispositivo (EN / PT-BR).
 * Sin entrada en el diccionario (o dispositivo en español) devuelve el texto
 * tal cual: el fallback ES es natural y los textos de negocio quedan intactos.
 */
export function t(texto: string): string {
  if (idioma === 'es') return texto
  const dic = idioma === 'pt' ? TRADUCCIONES_PT : TRADUCCIONES
  return Object.prototype.hasOwnProperty.call(dic, texto) ? dic[texto] : texto
}
